import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Plus, MessageSquare, Send, Menu, Trash2, Sparkles } from "lucide-react";
import { API } from "../lib/constants";

export default function WritingAssistant({ embedded = false, seriesOverride = null }) {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef(null);

  const SERIES_NAMES = {
    "the-red-ink-chronicles": "The Red Ink Chronicles",
    "the-notarys-shadow": "The Notary's Shadow",
    "signed-sealed-betrayed": "Signed, Sealed, Betrayed",
  };

  const SERIES_PROMPTS = {
    "the-red-ink-chronicles": [
      "Help me write a Hill Mode scene",
      "Enhance this dialogue for LEILAN",
      "Create tension in a hospital signing",
      "Write sound cues for a courthouse scene"
    ],
    "the-notarys-shadow": [
      "Write a tense scene between Vanessa and The Strategist",
      "Create noir atmosphere for a mobile notary visit",
      "Write dialogue for Teka discovering the pact",
      "Sound design cues for a suspenseful reveal"
    ],
    "signed-sealed-betrayed": [
      "Write a confrontation between Marcus and Selena",
      "Create tension in a Harlem night scene",
      "Enhance dialogue for Xavier's betrayal",
      "Sound cues for a dramatic chase sequence"
    ],
  };

  const seriesName = SERIES_NAMES[seriesOverride] || "Red Ink Studios";
  const seriesPrompts = SERIES_PROMPTS[seriesOverride] || SERIES_PROMPTS["the-red-ink-chronicles"];

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const params = seriesOverride ? `?series=${seriesOverride}` : "";
        const res = await axios.get(`${API}/chat/sessions${params}`);
        setSessions(res.data);
      } catch (e) { console.error(e); }
    };
    fetchSessions();
  }, [seriesOverride]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSession = (session) => {
    setCurrentSession(session);
    setMessages(session.messages || []);
  };

  const startNewChat = () => {
    setCurrentSession(null);
    setMessages([]);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: currentSession?.id, message: input, series: seriesOverride });
      const assistantMessage = { role: 'assistant', content: res.data.response };
      setMessages(prev => [...prev, assistantMessage]);
      if (!currentSession) {
        setCurrentSession({ id: res.data.session_id, title: res.data.title });
        setSessions(prev => [{ id: res.data.session_id, title: res.data.title }, ...prev]);
      }
    } catch (e) {
      console.error(e);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, there was an error. Please try again.' }]);
    } finally { setLoading(false); }
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/chat/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSession?.id === sessionId) startNewChat();
    } catch (e) { console.error(e); }
  };

  return (
    <div className={`${embedded ? 'h-[700px]' : 'min-h-screen pt-20'} flex`} data-testid="writing-page">
      <div className={`${sidebarOpen ? 'w-72' : 'w-0'} transition-all duration-300 overflow-hidden border-r border-white/5 bg-[#0a0a0a]`}>
        <div className="p-4">
          <button onClick={startNewChat} className="w-full btn-primary flex items-center justify-center gap-2 text-sm" data-testid="new-chat-btn"><Plus size={16} />New Chat</button>
        </div>
        <div className="overflow-y-auto h-[calc(100vh-140px)]">
          {sessions.map(session => (
            <div key={session.id} onClick={() => loadSession(session)}
              className={`p-4 cursor-pointer hover:bg-white/5 flex items-center justify-between group ${currentSession?.id === session.id ? 'bg-red-500/10 border-l-2 border-red-500' : ''}`}
              data-testid={`session-${session.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <MessageSquare size={16} className="text-gray-500 flex-shrink-0" />
                <span className="text-sm truncate">{session.title || 'New Chat'}</span>
              </div>
              <button onClick={(e) => deleteSession(session.id, e)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded">
                <Trash2 size={14} className="text-gray-500" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <div className="p-4 border-b border-white/5 flex items-center gap-4">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-white/10 rounded"><Menu size={20} /></button>
          <div>
            <h1 className="font-anton text-lg">AI Writing Assistant</h1>
            <p className="text-xs text-gray-500">Enhance your {seriesName} scripts</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <Sparkles size={48} className="mx-auto mb-6 text-red-500" />
                <h2 className="font-cinzel text-2xl mb-4">{seriesName} Writing Assistant</h2>
                <p className="text-gray-400 mb-8">I can help you enhance dialogue, fill story gaps, maintain character voices, and keep the cinematic audio drama tone consistent.</p>
                <div className="grid grid-cols-2 gap-3 text-left">
                  {seriesPrompts.map(prompt => (
                    <button key={prompt} onClick={() => setInput(prompt)} className="p-3 text-sm text-left bg-white/5 hover:bg-white/10 transition-colors">{prompt}</button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((msg, i) => (
                <div key={i} className={`chat-message ${msg.role}`} data-testid={`message-${i}`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              ))}
              {loading && (
                <div className="chat-message assistant">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/5">
          <div className="max-w-4xl mx-auto flex gap-4">
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Ask about story, dialogue, characters..." className="flex-1 bg-[#1a1a1a] border border-white/15 text-white placeholder:text-gray-500 px-4 py-3 focus:outline-none focus:border-red-500 transition-colors"
              disabled={loading} data-testid="chat-input" />
            <button onClick={sendMessage} disabled={loading || !input.trim()} className="btn-primary px-6 disabled:opacity-50" data-testid="send-btn"><Send size={18} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
